import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { rideSchema, signUpSchema } from '../utils/validationSchemas';
import { useAuthStore } from '../stores';

const useValidateForm = (formType, userData, prefillRide = null) => {
  // Global state.
  const { isAuth } = useAuthStore();

  // Choose appropriate schema and default values based on formType.
  const schema = formType === 'find-ride' ? rideSchema : signUpSchema;
  const defaultFormValues = useMemo(() => {
    if (formType === 'find-ride') {
      return {
        name: '',
        phone: '',
        passengers: 1,
        pickUp: '',
        dropOff: '',
        pickUpTime: 'ASAP',
      };
    } else {
      // sign-up form
      return {
        name: '',
        phone: '',
        email: '',
        password: '',
        confirmpassword: '',
      };
    }
  }, [formType]);

  // Set up form state. NOTE: the option is `defaultValues` — the previous
  // `defaultFormValues` key was silently ignored by React Hook Form, so the
  // defaults (passengers: 1, pickUpTime: 'ASAP') never applied.
  const formMethods = useForm({
    defaultValues: defaultFormValues,
    resolver: zodResolver(schema),
  });
  const { formState, register, getValues, setValue, handleSubmit } =
    formMethods;

  // Get errors from formState.
  const { errors } = formState;

  // Auto-fill form values if user is authenticated and data is available
  useEffect(() => {
    if (isAuth && userData && formType === 'find-ride') {
      const { name, phoneNumber } = userData.getRider;
      setValue('name', name);
      setValue('phone', phoneNumber);
    }
  }, [isAuth, userData, setValue, formType]);

  // Pre-fill form values from previous ride (e.g., "Request That Ride Again")
  useEffect(() => {
    if (prefillRide && formType === 'find-ride') {
      setValue('name', prefillRide.name || '');
      setValue('phone', prefillRide.phone || '');
      setValue('passengers', prefillRide.passengers || 1);
      setValue('pickUp', prefillRide.pickUp || '');
      setValue('dropOff', prefillRide.dropOff || '');
      setValue('pickUpTime', prefillRide.pickUpTime || 'ASAP');
    }
  }, [prefillRide, setValue, formType]);

  // Format phone number correctly.
  const handlePhoneChange = (e) => {
    const phoneNumber = e.target.value;

    // Remove all non-digit characters from the input
    const numericPhoneNumber = phoneNumber.replace(/\D/g, '');

    // Format the phone number as (123) 456-7890
    const formattedPhoneNumber = numericPhoneNumber.replace(
      /^(\d{3})(\d{3})(\d{4})$/,
      '($1) $2-$3'
    );

    // Set formatted phone number.
    setValue('phone', formattedPhoneNumber);
  };

  return { ...formMethods, errors, handlePhoneChange };
};

export default useValidateForm;
